import { useState, type ChangeEvent } from 'react';
import {
    ArrowLeft2,
    ArrowRight2,
    ArrowRotateLeft,
    Danger,
    Edit2,
    Spinner,
    Trash,
} from '@/components/icons';
import { AIBadge } from '@/components/ui/ai-badge';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import { cn } from '@/lib/utils';
import type { NarrativeDecision } from './guided-review-model';
import type { PublisherResultTableEntry } from '@/types/publisher-result-import';

/**
 * The pieces the guided review renders, with no say in which step is showing.
 *
 * Split off so `PublisherResultGuidedReview` reads as what it is — a walk
 * through the instruments of one import. The table chrome is one more step
 * removed, in `guided-review-tables`.
 */

export function WarningsAlert({ warnings }: { warnings: string[] }) {
    if (warnings.length === 0) return null;

    return (
        <Alert variant="warning" icon={<Danger className="size-4" />}>
            <p className="font-medium">Confira antes de aprovar</p>
            <ul className="mt-1.5 list-disc space-y-1 pl-4">
                {warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                ))}
            </ul>
        </Alert>
    );
}

/**
 * The paragraph a model wrote about one table, with the two ways to change it.
 *
 * Rendered inside the table's own card, under a rule — it is a section of that
 * card, not a card of its own, because it describes the numbers directly above
 * it and reaches the laudo attached to them.
 *
 * Keeping it is the default and has no button: the paragraph is already in the
 * laudo as far as this screen is concerned, so the psychologist only acts to
 * rewrite it or to leave it out. The step used to refuse to advance until each
 * paragraph had been answered for, which made a click out of an answer that was
 * "yes" almost every time.
 */
export function NarrativeSection({
    entry,
    decision,
    onDecide,
}: {
    entry: PublisherResultTableEntry;
    decision?: NarrativeDecision;
    onDecide: (decision: NarrativeDecision | undefined) => void;
}) {
    const original = entry.table.narrative?.trim() ?? '';
    const [draft, setDraft] = useState<string | null>(null);
    const isEditing = draft !== null;
    const isOmitted = decision?.kind === 'omit';
    const current = decision?.kind === 'edited' ? decision.text : original;

    return (
        <div className="mt-4 border-t border-border pt-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <AIBadge>Gerado automaticamente</AIBadge>

                {!isEditing &&
                    (isOmitted ? (
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                                Não entra no laudo
                            </span>
                            <TooltipIconButton
                                label="Voltar o texto para o laudo"
                                onClick={() => onDecide(undefined)}
                                className="text-muted-foreground hover:text-foreground"
                            >
                                <ArrowRotateLeft className="size-4" />
                            </TooltipIconButton>
                        </div>
                    ) : (
                        <div className="flex items-center gap-1">
                            <TooltipIconButton
                                label="Editar texto"
                                onClick={() => setDraft(current)}
                                className="text-muted-foreground hover:text-foreground"
                            >
                                <Edit2 className="size-4" />
                            </TooltipIconButton>
                            <TooltipIconButton
                                label="Entrar sem o texto"
                                onClick={() => onDecide({ kind: 'omit' })}
                                className="text-muted-foreground hover:text-foreground"
                            >
                                <Trash className="size-4" />
                            </TooltipIconButton>
                        </div>
                    ))}
            </div>

            {isEditing ? (
                <div className="space-y-2">
                    <Textarea
                        value={draft}
                        onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                            setDraft(event.target.value)
                        }
                        rows={5}
                        aria-label="Texto de apresentação"
                    />
                    <div className="flex flex-wrap gap-2">
                        <Button
                            type="button"
                            size="sm"
                            onClick={() => {
                                const text = draft.trim();
                                onDecide(text ? { kind: 'edited', text } : { kind: 'omit' });
                                setDraft(null);
                            }}
                        >
                            Salvar texto
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => setDraft(null)}
                        >
                            Cancelar
                        </Button>
                    </div>
                </div>
            ) : (
                <p
                    className={cn(
                        'text-sm leading-relaxed',
                        isOmitted ? 'text-muted-foreground line-through' : 'text-foreground',
                    )}
                >
                    {current}
                </p>
            )}
        </div>
    );
}

/** Where this file sits in a queue of imports waiting to be reviewed. */
export type FileNavigation = {
    position: number;
    total: number;
    onPrevious?: () => void;
    onNext?: () => void;
};

export function GuidedReviewFooter({
    approving,
    cancelling,
    canApprove,
    isFirstStep,
    isLastStep,
    fileNavigation,
    onDiscard,
    onBack,
    onContinue,
    onApprove,
}: {
    approving: boolean;
    cancelling: boolean;
    canApprove: boolean;
    isFirstStep: boolean;
    isLastStep: boolean;
    fileNavigation?: FileNavigation;
    onDiscard: () => void;
    onBack: () => void;
    onContinue: () => void;
    onApprove: () => void;
}) {
    return (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
                type="button"
                variant="ghostDestructive"
                onClick={onDiscard}
                disabled={cancelling || approving}
            >
                {cancelling ? (
                    <Spinner className="size-4 animate-spin" />
                ) : (
                    <Trash className="size-4" />
                )}
                Descartar importação
            </Button>

            <div className="flex flex-1 flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
                {fileNavigation && fileNavigation.total > 1 && (
                    <div className="flex items-center gap-1 sm:mr-2">
                        {fileNavigation.onPrevious && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={fileNavigation.onPrevious}
                                disabled={fileNavigation.position === 1 || approving}
                                aria-label="Arquivo anterior"
                                title="Arquivo anterior"
                            >
                                <ArrowLeft2 className="size-4" />
                            </Button>
                        )}
                        <span className="text-xs text-muted-foreground">
                            Arquivo {fileNavigation.position} de {fileNavigation.total}
                        </span>
                        {fileNavigation.onNext && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={fileNavigation.onNext}
                                disabled={
                                    fileNavigation.position === fileNavigation.total || approving
                                }
                                aria-label="Próximo arquivo"
                                title="Próximo arquivo"
                            >
                                <ArrowRight2 className="size-4" />
                            </Button>
                        )}
                    </div>
                )}
                <Button
                    type="button"
                    variant="outline"
                    onClick={onBack}
                    disabled={isFirstStep || approving}
                >
                    Voltar
                </Button>
                {isLastStep ? (
                    <Button type="button" onClick={onApprove} disabled={approving || !canApprove}>
                        {approving && <Spinner className="size-4 animate-spin" />}
                        Adicionar ao laudo
                    </Button>
                ) : (
                    <Button type="button" onClick={onContinue}>
                        Continuar
                    </Button>
                )}
            </div>
        </div>
    );
}
